import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import { ReactNode } from 'react';
import { cn } from '../../utils/cn';

type VerticalScrollAreaProps = {
  children: ReactNode;
  className?: string;
};

/**
 * A vertical scroll area component wrapping Radix UI ScrollArea.
 *
 * For horizontal scrolling with buttons and gradients, use `HorizontalScrollArea`.
 *
 * @param props.children - Content to be scrollable
 * @param props.className - Additional classes for the scroll root (typically height)
 *
 * @example
 * ```tsx
 * <VerticalScrollArea className="h-96">
 *   <DataTableNew.Table table={table} />
 * </VerticalScrollArea>
 * ```
 */
export const VerticalScrollArea = ({ children, className }: VerticalScrollAreaProps) => {
  return (
    <RadixScrollArea.Root className={cn('overflow-hidden', className)}>
      <RadixScrollArea.Viewport className="size-full rounded">{children}</RadixScrollArea.Viewport>

      <RadixScrollArea.Scrollbar
        className="flex touch-none bg-muted p-0.5 transition-colors duration-160 ease-out select-none hover:bg-muted data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=vertical]:w-2.5"
        orientation="vertical"
      >
        <RadixScrollArea.Thumb className="relative flex-1 rounded-lg bg-muted-foreground before:absolute before:top-1/2 before:left-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" />
      </RadixScrollArea.Scrollbar>

      <RadixScrollArea.Corner className="bg-blackA5" />
    </RadixScrollArea.Root>
  );
};
